import api from './api';

export interface Payment {
  id?: number;
  student_course: number;
  student_name?: string;
  student_fin?: string;
  company_name?: string;
  course_name?: string;
  period_year: number;
  period_month: number;
  period_display?: string;
  required_amount: number;
  paid_amount: number;
  debt_amount: number;
  remaining_amount?: number;
  due_date: string;
  status: 'gözləyir' | 'tam_ödənilməyib' | 'tam_ödənilib' | 'gecikir' | 'ləğv_edilib';
  description?: string;
  is_overdue?: boolean;
  days_overdue?: number;
  created_at?: string;
  updated_at?: string;
}

export interface PaymentTransaction {
  id?: number;
  payment: number;
  payment_info?: string;
  student_name?: string;
  amount: number;
  payment_date: string;
  payment_method: 'nəğd' | 'kart' | 'köçürmə' | 'online';
  receipt_number?: string;
  description?: string;
  created_at?: string;
}

export interface StudentCourse {
  id?: number;
  student: number;
  student_name?: string;
  course: number;
  course_name?: string;
  company_name?: string;
  start_date: string;
  end_date: string;
  payment_day: number;
  is_active: boolean;
  created_at?: string;
}

export interface PaymentFilters {
  search?: string;
  payment_status?: string;
  payment_method?: string;
  company?: number;
  overdue_only?: boolean;
  date_from?: string;
  date_to?: string;
}

export interface PaymentStatistics {
  total_payments: number;
  paid_payments: number;
  unpaid_payments: number;
  total_amount: number;
  unpaid_amount: number;
}

export const paymentAPI = {
  // Ödənişlərin siyahısını al
  getPayments: async (filters?: PaymentFilters): Promise<{ success: boolean; data?: Payment[]; message?: string }> => {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.payment_status) params.append('payment_status', filters.payment_status);
      if (filters?.company) params.append('company', filters.company.toString());
      if (filters?.overdue_only) params.append('overdue_only', filters.overdue_only.toString());
      if (filters?.date_from) params.append('date_from', filters.date_from);
      if (filters?.date_to) params.append('date_to', filters.date_to);

      const response = await api.get(`/payments/?${params.toString()}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Payments API Error:', error.response?.data);
      return { 
        success: false, 
        message: error.response?.data?.message || error.response?.data?.detail || 'Ödənişlər yüklənmədi' 
      };
    }
  },

  // Ödəniş əlavə et (PaymentTransaction yaradır)
  createPaymentTransaction: async (transaction: PaymentTransaction): Promise<{ success: boolean; data?: PaymentTransaction; message?: string; errors?: any }> => {
    try {
      const response = await api.post('/payment-transactions/', transaction);
      return response.data;
    } catch (error: any) {
      console.error('Create Payment Transaction API Error:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.detail || 'Ödəniş əlavə edilmədi',
        errors: error.response?.data?.errors || error.response?.data
      };
    }
  },

  // Ödəniş məlumatlarını yenilə
  updatePayment: async (id: number, payment: Partial<Payment>): Promise<{ success: boolean; data?: Payment; message?: string; errors?: any }> => {
    try {
      const response = await api.put(`/payments/${id}/`, payment);
      return response.data;
    } catch (error: any) {
      console.error('Update Payment API Error:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.detail || 'Ödəniş məlumatları yenilənmədi',
        errors: error.response?.data?.errors || error.response?.data
      };
    }
  },

  // Ödənişi sil
  deletePayment: async (id: number): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.delete(`/payments/${id}/`);
      return response.data;
    } catch (error: any) {
      console.error('Delete Payment API Error:', error.response?.data);
      return { 
        success: false, 
        message: error.response?.data?.message || error.response?.data?.detail || 'Ödəniş silinmədi' 
      };
    }
  },

  // Ödəniş statistikaları
  getStatistics: async (companyId?: number, year?: number): Promise<{ success: boolean; data?: PaymentStatistics; message?: string }> => {
    try {
      const params = new URLSearchParams();
      if (companyId) params.append('company', companyId.toString());
      if (year) params.append('year', year.toString());
      
      const response = await api.get(`/payments/statistics/?${params.toString()}`);
      return response.data; // Backend artıq { success: true, data: {...} } formatında qaytarır
    } catch (error: any) {
      console.error('Payment Statistics API Error:', error.response?.data);
      return { 
        success: false, 
        message: error.response?.data?.message || error.response?.data?.detail || 'Statistikalar yüklənmədi' 
      };
    }
  },

  // Student-Course əlaqələrini al
  getStudentCourses: async (filters?: { student?: number; course?: number; company?: number; is_active?: boolean }): Promise<{ success: boolean; data?: StudentCourse[]; message?: string }> => {
    try {
      const params = new URLSearchParams();
      if (filters?.student) params.append('student', filters.student.toString());
      if (filters?.course) params.append('course', filters.course.toString());
      if (filters?.company) params.append('company', filters.company.toString());
      if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());

      const response = await api.get(`/student-courses/?${params.toString()}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Student Courses API Error:', error.response?.data);
      return { 
        success: false, 
        message: error.response?.data?.message || error.response?.data?.detail || 'Tələbə kursları yüklənmədi' 
      };
    }
  },

  // PaymentTransaction-ları al
  getPaymentTransactions: async (paymentId?: number): Promise<{ success: boolean; data?: PaymentTransaction[]; message?: string }> => {
    try {
      const params = new URLSearchParams();
      if (paymentId) params.append('payment', paymentId.toString());

      const response = await api.get(`/payment-transactions/?${params.toString()}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Payment Transactions API Error:', error.response?.data);
      return { 
        success: false, 
        message: error.response?.data?.message || error.response?.data?.detail || 'Ödəniş əməliyyatları yüklənmədi' 
      };
    }
  },

  // PaymentTransaction sil
  deletePaymentTransaction: async (id: number): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.delete(`/payment-transactions/${id}/`);
      return response.data;
    } catch (error: any) {
      console.error('Delete Payment Transaction API Error:', error.response?.data);
      return { 
        success: false, 
        message: error.response?.data?.message || error.response?.data?.detail || 'Ödəniş əməliyyatı silinmədi' 
      };
    }
  },
};